import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ServiceFeeRow, ServiceFeeSaveRequest } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatAmount, formatDisplayDate, formatPercent, percentToRatio } from '@hq/utils';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Segmented,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import { useState } from 'react';
import { sumWidth } from '@hq/ui-kit';
import { hq } from '../session';

const FEE_TYPES: Record<string, string> = {
  STORAGE_FEE: '仓储费',
  DELIVERY_FEE: '配送费',
  SERVICE_FEE: '服务费',
  WRISTBAND_FEE: '腕带费'
};

const CHARGE_MODES: Record<string, string> = {
  BY_AMOUNT: '按金额比例',
  BY_QTY: '按数量',
  BY_ORDER: '按单',
  BY_DELIVERY: '按配送次数',
  FIXED_MONTH: '按月固定'
};

interface FeeFormValues extends Omit<ServiceFeeSaveRequest, 'effectiveFrom' | 'effectiveTo'> {
  range?: [Dayjs, Dayjs];
}

/**
 * 服务费规则（`cfg_service_fee`）：费率可配置、可设 0，免收仍出 0 元行留痕。
 * 规则由结单时的 SettlementService 读取，本页不参与计算。
 */
export default function ServiceFeeOps() {
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const [feeType, setFeeType] = useState('ALL');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<ServiceFeeRow | null>(null);
  const [form] = Form.useForm<FeeFormValues>();

  const list = useQuery({
    queryKey: ['service-fee', feeType],
    placeholderData: keepPreviousData,
    queryFn: () => hq.billing.serviceFee.list({ feeType: feeType === 'ALL' ? undefined : feeType })
  });

  const save = useMutation({
    mutationFn: (values: FeeFormValues) =>
      hq.billing.serviceFee.save({
        ...values,
        id: editing?.id,
        // 界面一律按百分数录入（5 表示 5%），报文按后端口径 0~1 提交
        rate: percentToRatio(values.rate),
        taxRate: percentToRatio(values.taxRate),
        effectiveFrom: values.range?.[0]?.format('YYYY-MM-DD'),
        effectiveTo: values.range?.[1]?.format('YYYY-MM-DD') ?? null
      }),
    onSuccess: async () => {
      message.success(editing ? '服务费规则已更新' : '服务费规则已新增');
      setOpen(false);
      setEditing(null);
      form.resetFields();
      await queryClient.invalidateQueries({ queryKey: ['service-fee'] });
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '保存失败')
  });

  const remove = useMutation({
    mutationFn: (id: string) => hq.billing.serviceFee.remove(id),
    onSuccess: async () => {
      message.success('规则已停用');
      await queryClient.invalidateQueries({ queryKey: ['service-fee'] });
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '删除失败')
  });

  const openForm = (row?: ServiceFeeRow) => {
    setEditing(row ?? null);
    form.resetFields();
    if (row) {
      form.setFieldsValue({
        ruleCode: row.ruleCode,
        ruleName: row.ruleName,
        orgId: row.orgId,
        supplierId: row.supplierId === '0' ? undefined : row.supplierId,
        feeType: row.feeType,
        chargeMode: row.chargeMode,
        // 存量是 0~1 小数，表单按百分数展示
        rate: row.rate * 100,
        unitPrice: row.unitPrice,
        fixedAmount: row.fixedAmount,
        minAmount: row.minAmount,
        maxAmount: row.maxAmount,
        freeFlag: row.freeFlag,
        taxRate: row.taxRate * 100,
        status: row.status,
        remark: row.remark,
        range: [row.effectiveFrom ? dayjs(row.effectiveFrom) : dayjs(), row.effectiveTo ? dayjs(row.effectiveTo) : dayjs().add(1, 'year')]
      });
    } else {
      form.setFieldsValue({
        feeType: 'SERVICE_FEE',
        chargeMode: 'BY_AMOUNT',
        rate: 5,
        taxRate: 6,
        status: 'ACTIVE',
        freeFlag: false,
        range: [dayjs(), dayjs().add(1, 'year')]
      });
    }
    setOpen(true);
  };

  const columns: ColumnsType<ServiceFeeRow> = [
    { title: '规则编码', dataIndex: 'ruleCode', width: 150 },
    { title: '规则名称', dataIndex: 'ruleName', width: 200, ellipsis: true },
    {
      title: '计费类型',
      dataIndex: 'feeType',
      width: 110,
      render: (value: string) => <Tag color="blue">{FEE_TYPES[value] ?? value}</Tag>
    },
    { title: '计费方式', dataIndex: 'chargeMode', width: 130, render: (value: string) => CHARGE_MODES[value] ?? value },
    {
      title: '费率 / 税率',
      key: 'rate',
      width: 140,
      align: 'right',
      // 后端存 0~1 小数：直接拼 % 会把 0.05 显示成 "0.05%"，必须用 formatPercent
      render: (_v, row) => (
        <Space direction="vertical" size={0} style={{ textAlign: 'right' }}>
          <span>{row.rate > 0 ? formatPercent(row.rate) : '-'}</span>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            税率 {formatPercent(row.taxRate)}
          </Typography.Text>
        </Space>
      )
    },
    {
      title: '单价 / 固定额',
      key: 'amount',
      width: 210,
      align: 'right',
      render: (_v, row) => (
        <Space direction="vertical" size={0} style={{ textAlign: 'right' }}>
          <span>{formatAmount(row.unitPrice, 4)}</span>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            固定 {formatAmount(row.fixedAmount)}
          </Typography.Text>
        </Space>
      )
    },
    {
      title: '免收',
      dataIndex: 'freeFlag',
      width: 90,
      render: (value: boolean) =>
        value ? (
          <Tag color="gold">免收（出 0 元行）</Tag>
        ) : (
          '-'
        )
    },
    {
      title: '有效期',
      key: 'period',
      width: 200,
      render: (_v, row) => `${formatDisplayDate(row.effectiveFrom)} ~ ${row.effectiveTo ? formatDisplayDate(row.effectiveTo) : '长期'}`
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 90,
      render: (value: string) => (value === 'ACTIVE' ? <Tag color="green">生效</Tag> : <Tag>停用</Tag>)
    },
    {
      title: '操作',
      key: 'action',
      width: 140,
      fixed: 'right',
      render: (_v, row) => (
        <Space size={4}>
          <Button size="small" type="link" icon={<EditOutlined />} onClick={() => openForm(row)}>
            编辑
          </Button>
          <Button
            size="small"
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() =>
              modal.confirm({
                title: '停用该规则？',
                content: '停用后新结单不再引用；已生成的对账单不受影响。',
                okText: '停用',
                okButtonProps: { danger: true },
                cancelText: '取消',
                onOk: () => remove.mutateAsync(row.id)
              })
            }
          />
        </Space>
      )
    }
  ];

  return (
    <Card
      className="page-card"
      title={
        <Space>
          服务费规则
          <Typography.Text type="secondary">可配置费率、可设 0；免收仍出 0 元行留痕</Typography.Text>
        </Space>
      }
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => list.refetch()} loading={list.isFetching}>
            刷新
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openForm()}>
            新增规则
          </Button>
        </Space>
      }
    >
      <Segmented
        value={feeType}
        onChange={(value) => setFeeType(String(value))}
        options={[{ label: '全部', value: 'ALL' }, ...Object.entries(FEE_TYPES).map(([value, label]) => ({ value, label }))]}
        style={{ marginBottom: 12 }}
      />

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="口径说明"
        description="费率与税率一律按百分数填写（填 5 表示 5%）；按数量/按单/按配送填单价；按月固定填固定额。免收时费率与金额均置 0，但仍会生成 0 元结算行，避免对账缺行扯皮。"
      />

      {list.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="服务费规则加载失败"
          description={list.error instanceof ApiError ? list.error.friendlyMessage : String(list.error)}
        />
      ) : null}

      <Table<ServiceFeeRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={list.data ?? []}
        loading={list.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
        locale={{ emptyText: list.isError ? '加载失败，请重试' : '暂无服务费规则' }}
      />

      <Modal
        title={editing ? '编辑服务费规则' : '新增服务费规则'}
        open={open}
        onCancel={() => setOpen(false)}
        okText="保存"
        cancelText="取消"
        confirmLoading={save.isPending}
        onOk={() => form.submit()}
        width={720}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" onFinish={(values) => save.mutate(values)}>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="ruleCode" label="规则编码" rules={[{ required: true, message: '必填' }]}>
                <Input maxLength={64} disabled={Boolean(editing)} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="ruleName" label="规则名称" rules={[{ required: true, message: '必填' }]}>
                <Input maxLength={128} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="feeType" label="计费类型" rules={[{ required: true }]}>
                <Select options={Object.entries(FEE_TYPES).map(([value, label]) => ({ value, label }))} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="chargeMode" label="计费方式" rules={[{ required: true }]}>
                <Select options={Object.entries(CHARGE_MODES).map(([value, label]) => ({ value, label }))} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="status" label="状态">
                <Select
                  options={[
                    { value: 'ACTIVE', label: '生效' },
                    { value: 'STOP', label: '停用' }
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="rate" label="费率（%）" tooltip="填 5 表示 5%">
                <InputNumber min={0} max={100} step={0.5} precision={2} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="unitPrice" label="单价（按数量/单/次）">
                <InputNumber min={0} precision={4} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="fixedAmount" label="固定额（按月）">
                <InputNumber min={0} precision={2} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="minAmount" label="单期下限">
                <InputNumber min={0} precision={2} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="maxAmount" label="单期上限（0=不限）">
                <InputNumber min={0} precision={2} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="taxRate" label="税率（%）" tooltip="填 6 表示 6%">
                <InputNumber min={0} max={100} step={1} precision={2} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="freeFlag" label="免收（出 0 元行）" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item name="range" label="生效期间" rules={[{ required: true, message: '请选择生效起' }]}>
                <DatePicker.RangePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="remark" label="备注">
                <Input.TextArea rows={2} maxLength={255} showCount placeholder="如：集采降价后服务费调整为 3%" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </Card>
  );
}
